'use client';

import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  organization: string;
  role: string;
  organizationType: string;
  challenge: string;
  discussionTopics: string[];
  preferredTimeline: string;
  additionalInfo: string;
}

interface FormErrors {
  [key: string]: string;
}

const ContactForm: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    organization: '',
    role: '',
    organizationType: '',
    challenge: '',
    discussionTopics: [],
    preferredTimeline: '',
    additionalInfo: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // Rate limiting
  const submissionTimestamp = useRef<number>(0);
  const MIN_SUBMISSION_INTERVAL = 30000; // 30 seconds between submissions

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const organizationTypes = [
    'Government Ministry/Department',
    'State-Owned Enterprise',
    'Regulatory Authority',
    'Development/Donor Program',
    'Public Institution',
    'Other',
  ];

  const discussionTopicOptions = [
    'Strategic Planning & Institutional Design',
    'Leadership Development & Executive Capacity',
    'Organizational Transformation',
    'Performance Management Systems',
    'Change Management & Execution',
    'AI Literacy & Technology Integration',
    'Governance & Decision-Making Frameworks',
  ];

  const timelineOptions = [
    'Within 1-2 weeks',
    'Within 1 month',
    'Within 2-3 months',
    'Exploratory - No immediate timeline',
  ];

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.organization.trim()) {
      newErrors.organization = 'Organization name is required';
    }

    if (!formData.role.trim()) {
      newErrors.role = 'Role/title is required';
    }

    if (!formData.organizationType) {
      newErrors.organizationType = 'Please select organization type';
    }

    if (!formData.challenge.trim()) {
      newErrors.challenge = 'Please describe your transformation challenge';
    } else if (formData.challenge.trim().length < 50) {
      newErrors.challenge = 'Please provide more detail (minimum 50 characters)';
    }

    if (formData.discussionTopics.length === 0) {
      newErrors.discussionTopics = 'Please select at least one discussion topic';
    }

    if (!formData.preferredTimeline) {
      newErrors.preferredTimeline = 'Please select a preferred timeline';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleTopicToggle = (topic: string) => {
    setFormData((prev) => {
      const topics = prev.discussionTopics.includes(topic)
        ? prev.discussionTopics.filter((t) => t !== topic)
        : [...prev.discussionTopics, topic];
      return { ...prev, discussionTopics: topics };
    });
    if (errors.discussionTopics) {
      setErrors((prev) => ({ ...prev, discussionTopics: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Rate limiting protection
    const now = Date.now();
    const timeSinceLastSubmission = now - submissionTimestamp.current;
    
    if (timeSinceLastSubmission < MIN_SUBMISSION_INTERVAL && submissionTimestamp.current !== 0) {
      setSubmitError(`Please wait ${Math.ceil((MIN_SUBMISSION_INTERVAL - timeSinceLastSubmission) / 1000)} seconds before submitting again.`);
      return;
    }

    if (!validateForm()) {
      // Scroll to first error
      const firstError = Object.keys(errors)[0];
      const errorElement = document.getElementById(firstError);
      errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);
    submissionTimestamp.current = now;

    try {
      const formPayload = new FormData();
      formPayload.append('fullName', formData.fullName);
      formPayload.append('email', formData.email);
      formPayload.append('phone', formData.phone);
      formPayload.append('organization', formData.organization);
      formPayload.append('role', formData.role);
      formPayload.append('organizationType', formData.organizationType);
      formPayload.append('challenge', formData.challenge);
      formPayload.append('discussionTopics', formData.discussionTopics.join(', '));
      formPayload.append('preferredTimeline', formData.preferredTimeline);
      formPayload.append('additionalInfo', formData.additionalInfo);
      // Formspree official honeypot field (must be empty)
      formPayload.append('_gotcha', '');

      const response = await fetch('https://formspree.io/f/mwvddgkz', {
        method: 'POST',
        body: formPayload,
        headers: {
          Accept: 'application/json',
        },
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          organization: '',
          role: '',
          organizationType: '',
          challenge: '',
          discussionTopics: [],
          preferredTimeline: '',
          additionalInfo: '',
        });
        window.scrollTo({ top: document.getElementById('contact-form')?.offsetTop ?? 0, behavior: 'smooth' });
      } else {
        const data = await response.json().catch(() => ({}));
        const message = (data as { error?: string })?.error || 'Submission failed. Please email info@diaax.com.';
        setSubmitError(message);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitError('An unexpected error occurred. Please email info@diaax.com.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isHydrated) {
    return (
      <div className="bg-white rounded-lg institutional-shadow p-5 sm:p-8 lg:p-12">
        <div className="animate-pulse space-y-6">
          <div className="h-12 bg-muted rounded"></div>
          <div className="h-12 bg-muted rounded"></div>
          <div className="h-32 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div id="contact-form" className="bg-white rounded-lg institutional-shadow p-5 sm:p-8 lg:p-12">
        <div className="flex flex-col items-center text-center py-8 sm:py-12">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
            <Icon name="CheckCircleIcon" size={40} className="text-green-600" variant="solid" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-text-primary mb-3">
            Thank You for Reaching Out
          </h2>
          <p className="text-base sm:text-lg text-text-secondary max-w-md mb-6">
            Your message has been received. We will review your request and get back to you within <strong className="text-text-primary">1–2 business days</strong>.
          </p>
          <div className="bg-surface border border-border rounded-lg px-6 py-4 max-w-sm w-full mb-8">
            <p className="text-sm text-text-secondary">
              In the meantime, feel free to explore our{' '}
              <a href="/services" className="text-institutional font-medium hover:underline">
                services
              </a>{' '}
              or{' '}
              <a href="/case-studies" className="text-institutional font-medium hover:underline">
                case studies
              </a>
              .
            </p>
          </div>
          <button
            onClick={() => setSubmitSuccess(false)}
            className="px-6 py-3 border border-institutional text-institutional font-medium rounded-md hover:bg-institutional hover:text-institutional-foreground transition-fast text-sm"
          >
            Submit Another Request
          </button>
        </div>
      </div>
    );
  }

  return (
    <div id="contact-form" className="bg-white rounded-lg institutional-shadow p-5 sm:p-8 lg:p-12">
      {submitSuccess && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md flex items-start gap-3">
          <Icon name="CheckCircleIcon" size={20} className="text-green-600 flex-shrink-0 mt-0.5" variant="solid" />
          <div className="flex-1">
            <p className="text-sm text-green-800 font-medium">Thank you. Your message has been sent successfully.</p>
          </div>
        </div>
      )}

      {submitError && (
        <div className="mb-6 p-4 bg-error/10 border border-error rounded-md flex items-start gap-3">
          <Icon name="ExclamationCircleIcon" size={20} className="text-error flex-shrink-0 mt-0.5" variant="solid" />
          <div className="flex-1">
            <p className="text-sm text-error font-medium">{submitError}</p>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
        {/* Formspree honeypot field - hidden from users, must remain empty */}
        <div style={{ display: 'none' }} aria-hidden="true">
          <input type="text" name="_gotcha" tabIndex={-1} autoComplete="off" defaultValue="" />
        </div>

        {/* Personal Information */}
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-4 sm:mb-6 institutional-accent-border pl-4">
            Personal Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-text-primary mb-2">
                Full Name <span className="text-error">*</span>
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border text-sm sm:text-base ${
                  errors.fullName ? 'border-error' : 'border-input'
                } rounded-md focus:outline-none focus:ring-2 focus:ring-ring transition-fast`}
                placeholder="Dr. Sarah Johnson"
              />
              {errors.fullName && (
                <p className="mt-1 text-xs sm:text-sm text-error">{errors.fullName}</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
                Email Address <span className="text-error">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border text-sm sm:text-base ${
                  errors.email ? 'border-error' : 'border-input'
                } rounded-md focus:outline-none focus:ring-2 focus:ring-ring transition-fast`}
                placeholder="sarah.johnson@ministry.gov"
              />
              {errors.email && <p className="mt-1 text-xs sm:text-sm text-error">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-text-primary mb-2">
                Phone Number (Optional)
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring transition-fast text-sm sm:text-base"
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium text-text-primary mb-2">
                Role/Title <span className="text-error">*</span>
              </label>
              <input
                type="text"
                id="role"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border text-sm sm:text-base ${
                  errors.role ? 'border-error' : 'border-input'
                } rounded-md focus:outline-none focus:ring-2 focus:ring-ring transition-fast`}
                placeholder="Director of Strategic Planning"
              />
              {errors.role && <p className="mt-1 text-xs sm:text-sm text-error">{errors.role}</p>}
            </div>
          </div>
        </div>

        {/* Institutional Information */}
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-4 sm:mb-6 institutional-accent-border pl-4">
            Institutional Information
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <label htmlFor="organization" className="block text-sm font-medium text-text-primary mb-2">
                Organization Name <span className="text-error">*</span>
              </label>
              <input
                type="text"
                id="organization"
                name="organization"
                value={formData.organization}
                onChange={handleInputChange}
                className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border text-sm sm:text-base ${
                  errors.organization ? 'border-error' : 'border-input'
                } rounded-md focus:outline-none focus:ring-2 focus:ring-ring transition-fast`}
                placeholder="Ministry of Economic Development"
              />
              {errors.organization && (
                <p className="mt-1 text-xs sm:text-sm text-error">{errors.organization}</p>
              )}
            </div>

            <div>
              <label htmlFor="organizationType" className="block text-sm font-medium text-text-primary mb-2">
                Organization Type <span className="text-error">*</span>
              </label>
              <select
                id="organizationType"
                name="organizationType"
                value={formData.organizationType}
                onChange={handleInputChange}
                className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border text-sm sm:text-base ${
                  errors.organizationType ? 'border-error' : 'border-input'
                } rounded-md focus:outline-none focus:ring-2 focus:ring-ring transition-fast`}
              >
                <option value="">Select organization type</option>
                {organizationTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              {errors.organizationType && (
                <p className="mt-1 text-xs sm:text-sm text-error">{errors.organizationType}</p>
              )}
            </div>
          </div>
        </div>

        {/* Transformation Challenge */}
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-4 sm:mb-6 institutional-accent-border pl-4">
            Transformation Challenge
          </h3>
          <div>
            <label htmlFor="challenge" className="block text-sm font-medium text-text-primary mb-2">
              Describe Your Strategic Challenge <span className="text-error">*</span>
            </label>
            <textarea
              id="challenge"
              name="challenge"
              value={formData.challenge}
              onChange={handleInputChange}
              rows={5}
              className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border text-sm sm:text-base ${
                errors.challenge ? 'border-error' : 'border-input'
              } rounded-md focus:outline-none focus:ring-2 focus:ring-ring transition-fast`}
              placeholder="Please describe the institutional transformation challenge you are facing, including context, current constraints, and desired outcomes. (Minimum 50 characters)"
            />
            {errors.challenge && (
              <p className="mt-1 text-xs sm:text-sm text-error">{errors.challenge}</p>
            )}
            <p className="mt-2 text-xs sm:text-sm text-text-secondary">
              Character count: {formData.challenge.length} / 50 minimum
            </p>
          </div>
        </div>

        {/* Discussion Topics */}
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-text-primary mb-4 sm:mb-6 institutional-accent-border pl-4">
            Preferred Discussion Topics
          </h3>
          <p className="text-xs sm:text-sm text-text-secondary mb-3 sm:mb-4">
            Select all areas you would like to explore in our initial conversation <span className="text-error">*</span>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {discussionTopicOptions.map((topic) => (
              <label
                key={topic}
                className="flex items-start gap-3 p-3 sm:p-4 border border-input rounded-md cursor-pointer hover:bg-surface transition-fast"
              >
                <input
                  type="checkbox"
                  checked={formData.discussionTopics.includes(topic)}
                  onChange={() => handleTopicToggle(topic)}
                  className="mt-0.5 w-4 h-4 text-institutional border-input rounded focus:ring-2 focus:ring-ring flex-shrink-0"
                />
                <span className="text-xs sm:text-sm text-text-primary">{topic}</span>
              </label>
            ))}
          </div>
          {errors.discussionTopics && (
            <p className="mt-2 text-xs sm:text-sm text-error">{errors.discussionTopics}</p>
          )}
        </div>

        {/* Timeline & Additional Information */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          <div>
            <label htmlFor="preferredTimeline" className="block text-sm font-medium text-text-primary mb-2">
              Preferred Timeline <span className="text-error">*</span>
            </label>
            <select
              id="preferredTimeline"
              name="preferredTimeline"
              value={formData.preferredTimeline}
              onChange={handleInputChange}
              className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 border text-sm sm:text-base ${
                errors.preferredTimeline ? 'border-error' : 'border-input'
              } rounded-md focus:outline-none focus:ring-2 focus:ring-ring transition-fast`}
            >
              <option value="">Select timeline</option>
              {timelineOptions.map((timeline) => (
                <option key={timeline} value={timeline}>
                  {timeline}
                </option>
              ))}
            </select>
            {errors.preferredTimeline && (
              <p className="mt-1 text-xs sm:text-sm text-error">{errors.preferredTimeline}</p>
            )}
          </div>

          <div>
            <label htmlFor="additionalInfo" className="block text-sm font-medium text-text-primary mb-2">
              Additional Information (Optional)
            </label>
            <textarea
              id="additionalInfo"
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring transition-fast text-sm sm:text-base"
              placeholder="Any additional context or specific questions you would like to address"
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-4 sm:pt-6 border-t border-border">
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-institutional text-institutional-foreground font-semibold rounded-md hover:bg-institutional/90 transition-fast institutional-shadow disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            {isSubmitting ? (
              <>
                <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
                <span>Submitting Request...</span>
              </>
            ) : (
              <>
                <span>Submit Conversation Request</span>
                <Icon name="ArrowRightIcon" size={18} />
              </>
            )}
          </button>
          <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-text-secondary">
            By submitting this form, you acknowledge that your information will be handled confidentially in accordance with our data protection standards.
          </p>
          <p className="mt-2 sm:mt-3 text-xs text-text-secondary italic">
            All institutional conversations are treated with professional discretion appropriate for senior-level advisory engagements.
          </p>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;